import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchMap,
  resetState,
  selectiDB,
  selectMap,
  selectVersion,
  updateState,
} from "../../features/counter/counterSlice";
import { getChapterRootFromXml } from "../../components/GuidelineNav/menuGeneration";
import { getMenuItems } from "../../components/GuidelineNav/guidelineMenuGenerator";
import { isNullOrEmpty } from "../../utils/isNullOrEmpty";

const LinkType = {
  EXTERNAL: "external",
  INTRA_GUIDELINE: "intraGuideline",
  INTER_GUIDELINE: "interGuideline",
  NAV_GUIDELINE: "navGuideline",
  PDF: "pdf",
  UNKNOWN: "unknown",
};

export const useUpdatesClickAction = (ref, closePopup) => {
  const handlersRef = useRef([]);
  const closePopupRef = useRef(closePopup);
  useEffect(() => {
    closePopupRef.current = closePopup;
  }, [closePopup]);
  const navigateRef = useRef(null);
  let dartSelectors = {};
  dartSelectors.map = useSelector(selectMap);
  dartSelectors.ccmsversion = useSelector(selectVersion);
  dartSelectors.iDB = useSelector(selectiDB);
  dartSelectors.dispatch = useDispatch();
  dartSelectors.location = useLocation();
  dartSelectors.navigate = useNavigate();

  // Fetch menu XML data
  const getMenuXml = useCallback(
    async (glPage) => {
      const contentLink = `${glPage}.txt`;

      try {
        // Try to get from IndexedDB first
        const cachedData = await dartSelectors.iDB.read(contentLink);
        if (cachedData) {
          console.log("Loaded from cache:", contentLink);
          return cachedData;
        }

        // Fetch from server
        const action = await dartSelectors.dispatch(
          fetchMap({
            path: contentLink,
            dartSelectors,
          }),
        );

        const data = action.payload?.data;
        if (!data) {
          throw new Error(`No content data received for ${contentLink}`);
        }
        dartSelectors.dispatch(updateState({ map: data }));
        return data;
      } catch (err) {
        console.error(`Failed to load content for ${contentLink}:`, err);
        throw err;
      }
    },
    [dartSelectors.iDB, dartSelectors.dispatch, dartSelectors],
  );

  // Find matching msPath in chapter data
  const findMsPath = useCallback((chapterNode, href) => {
    if (isNullOrEmpty(chapterNode)) return null;

    const hrefWithoutHash = href.split("#")[0];

    const nodeMatches =
      (!isNullOrEmpty(chapterNode.href) &&
        chapterNode.href.includes(hrefWithoutHash)) ||
      (!isNullOrEmpty(chapterNode.id) &&
        decodeURIComponent(chapterNode.id).includes(hrefWithoutHash));

    if (nodeMatches) {
      return chapterNode.msPath;
    }

    // Recursively search children
    if (chapterNode.children?.length > 0) {
      for (const child of chapterNode.children) {
        const found = findMsPath(child, href);
        if (found) return found;
      }
    }

    return null;
  }, []);

  // Retrieve URL from XML data
  const retrieveUrl = useCallback(
    (xmlData, href) => {
      const chapter = getChapterRootFromXml(xmlData);
      const chapterData = getMenuItems(chapter);

      // Search through all chapters
      for (const item of chapterData) {
        const matchedNode = findMsPath(item, href);
        if (matchedNode) {
          return matchedNode;
        }
      }

      console.warn("No matching node found for href:", href);
      return null;
    },
    [findMsPath],
  );

  // Handle inter-guideline links (different guideline)
  const handleInterGuideline = useCallback(
    async (link) => {
      try {
        const href = link.getAttribute("href");

        // Extract guideline and chapter path
        const guideline = href.replaceAll("../", "").split("/")[0];
        const chapterHref = href.replace(
          new RegExp(`(\\.\\./)+${guideline}/`),
          "",
        );

        // Fetch menu data for the target guideline
        const content = await getMenuXml(guideline);

        if (content) {
          const targetUrl = retrieveUrl(content, chapterHref);

          if (targetUrl) {
            const currentUrl =
              dartSelectors.location.pathname +
              dartSelectors.location.search +
              dartSelectors.location.hash;
            if (currentUrl === targetUrl) {
              // Already on this URL — trigger hash-toggle so Counter.jsx's
              // scroll_view() fires correctly via React Router location change.
              reNavigateSameUrlHash(dartSelectors.navigate, targetUrl);
            } else {
              dartSelectors.dispatch(resetState());
              dartSelectors.navigate(targetUrl);
            }
          } else {
            console.error("Could not find target URL for:", chapterHref);
          }
        }
      } catch (error) {
        console.error("Error handling inter-guideline link:", error);
      }
    },
    [getMenuXml, retrieveUrl, dartSelectors.navigate, dartSelectors.location],
  );

  // Handle intra-guideline links (same guideline, different chapter/section)
  const handleIntraGuideline = useCallback(
    async (link) => {
      try {
        const href = link.getAttribute("href") || "";
        const chapterHref = href.replace(/^((\.\.\/)+)/, "");

        const searchParams = new URLSearchParams(dartSelectors.location.search);
        const guideline = searchParams.get("guidelinePage");
        if (!guideline) {
          console.error("Could not resolve current guidelinePage for:", href);
          return;
        }

        const content = await getMenuXml(guideline);
        if (!content) return;

        const targetUrl = retrieveUrl(content, chapterHref);
        if (!targetUrl) {
          console.error("Could not find target URL for:", chapterHref);
          return;
        }

        const currentUrl =
          dartSelectors.location.pathname +
          dartSelectors.location.search +
          dartSelectors.location.hash;

        if (currentUrl === targetUrl) {
          reNavigateSameUrlHash(dartSelectors.navigate, targetUrl);
        } else {
          dartSelectors.navigate(targetUrl);
        }
      } catch (error) {
        console.error("Error handling intra-guideline link:", error);
      }
    },
    [getMenuXml, retrieveUrl, dartSelectors.navigate, dartSelectors.location],
  );
  // Handle guideline links at the homepage
  const handleNavGuideline = useCallback(
    async (link) => {
      try {
        const href = link.getAttribute("href");
        dartSelectors.navigate(`/updates?select=${href}`);
      } catch (error) {
        console.error("Error handling inter-guideline link:", error);
      }
    },
    [getMenuXml, retrieveUrl, dartSelectors.navigate],
  );

  // Handle external links
  const handleExternalLink = useCallback((link) => {
    const href = link.getAttribute("href");
    window.open(href, "_blank", "noopener,noreferrer");
  }, []);

  // Handle PDF links
  const handlePdfLink = useCallback((link) => {
    const href = link.getAttribute("href");
    const targetUrl = `${import.meta.env.REACT_APP_CCMS_BASE_URL}/${dartSelectors.ccmsversion.selected}/${href.replaceAll("../", "")}`;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  }, []);

  // Route link handling based on type
  const handleLinkByType = useCallback(
    async (link, type) => {
      switch (type) {
        case LinkType.EXTERNAL:
          handleExternalLink(link);
          break;
        case LinkType.INTER_GUIDELINE:
          await handleInterGuideline(link);
          break;
        case LinkType.INTRA_GUIDELINE:
          await handleIntraGuideline(link);
          break;
        case LinkType.NAV_GUIDELINE:
          await handleNavGuideline(link);
          break;
        case LinkType.PDF:
          await handlePdfLink(link);
          break;
        default:
          console.warn("Unknown link type:", type);
      }
    },
    [handleExternalLink, handleInterGuideline, handleIntraGuideline, handlePdfLink],
  );

  // Determine link scope/type
  const checkLinkScope = useCallback((link) => {
    const scope = link.getAttribute("scope");
    const href = link.getAttribute("href");
    const format = link.getAttribute("format");

    if (scope === LinkType.EXTERNAL) {
      return LinkType.EXTERNAL;
    }

    if (format && format.toLowerCase() === LinkType.PDF) {
      return LinkType.PDF;
    }

    if (href.startsWith("http://") || href.startsWith("https://")) {
      return LinkType.EXTERNAL;
    }
    // Obviously this pattern ../ can repeat 2 - 3 times for inter guideline URL
    if (href.startsWith("../../")) {
      return LinkType.INTER_GUIDELINE;
    }
    if (href.startsWith("../")) {
      return LinkType.INTRA_GUIDELINE;
    }
    if (/^[^/]+\.dita$/.test(href)) {
      return LinkType.NAV_GUIDELINE;
    }
    return LinkType.UNKNOWN;
  }, []);

  const clickAction = useCallback(
    (root) => {
      if (!root) return;

      const linkList = root.querySelectorAll("xref");

      handlersRef.current.forEach(({ link, handler }) => {
        link.removeEventListener("click", handler);
      });
      handlersRef.current = [];

      linkList.forEach((link) => {
        const handler = async (e) => {
          e.preventDefault();
          closePopupRef.current?.();

          try {
            const linkType = checkLinkScope(link);
            await handleLinkByType(link, linkType);
          } catch (error) {
            console.error("Error handling link click:", error);
          }
        };

        link.addEventListener("click", handler);
        handlersRef.current.push({ link, handler });
      });
    },
    [checkLinkScope, handleLinkByType],
  );

  // Keep navigateRef current so the capturing handler always uses the
  // latest navigate function without needing it as a useEffect dependency.
  navigateRef.current = dartSelectors.navigate;

  // Setup and cleanup effect
  useEffect(() => {
    if (ref?.current) {
      clickAction(ref.current);
    }

    // Cleanup function
    return () => {
      handlersRef.current.forEach(({ link, handler }) => {
        link.removeEventListener("click", handler);
      });
      handlersRef.current = [];
    };
  }, [ref, clickAction]);

  // Capturing listener on the popup container to intercept .xrefLink clicks
  // that have been converted from <xref> by HandleSectionClick.
  // Capturing phase fires BEFORE jQuery's bubbling handler, so
  // stopPropagation() prevents changeGuideline() from clearing page content.
  useEffect(() => {
    const container = ref?.current;
    if (!container) return;

    const captureHandler = (e) => {
      const eventTarget = e.target;
      if (!(eventTarget instanceof Element)) return;

      const link = eventTarget.closest("a.xrefLink[href]");
      if (!link || !container.contains(link)) return;

      e.preventDefault();
      // Block global xref handlers (contentsection.jsx) from running and
      // dispatching changeGuideline()/reset flows that clear main content.
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
      e.stopPropagation();
      closePopupRef.current?.();

      const href = link.getAttribute("href");
      if (!href) return;

      // For ../../ links the formatted href can carry a stale topicfile when
      // the user arrived via the menu. Use unformattedhref so handleInterGuideline
      // resolves the correct target URL.
      const rawHref = link.getAttribute("unformattedhref");
      if (rawHref?.startsWith("../../")) {
        const tempLink = document.createElement("a");
        tempLink.setAttribute("href", rawHref);
        handleInterGuideline(tempLink);
        return;
      }

      const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      if (href === currentUrl) {
        reNavigateSameUrlHash(navigateRef.current, href);
      } else {
        navigateRef.current?.(href);
      }
    };

    container.addEventListener("click", captureHandler, true);
    return () => container.removeEventListener("click", captureHandler, true);
  });

  return { clickAction };
};

function reNavigateSameUrlHash(navigate, href) {
  if (!navigate || !href) return;
  try {
    const parsed = new URL(href, window.location.origin);
    const targetPathSearch = parsed.pathname + parsed.search;
    const targetHash = parsed.hash || "";
    const currentPathSearch = window.location.pathname + window.location.search;

    if (!targetHash) {
      navigate(targetPathSearch || href);
      return;
    }

    if (currentPathSearch === targetPathSearch && window.location.hash === targetHash) {
      // Drop hash first, then restore it on next tick so the existing hash
      // listeners/scroll behavior in the app can fire again.
      navigate(targetPathSearch, { replace: true });
      setTimeout(() => {
        navigate(targetPathSearch + targetHash, { replace: true });
      }, 0);
      return;
    }

    navigate(targetPathSearch + targetHash);
  } catch (_error) {
    navigate(href);
  }
}
